import React, { useCallback, useEffect, useState } from "react";

import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Row,
  InputGroup,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import Header from "../../components/Header";
import HeaderTitle from "../../components/HeaderTitle";
import { useInvestors } from "../../providers/investorsProvider";
import { usersApi } from "../../services/userServices";
import Loader from "../../components/Loader";
import InvestorModalDetails from "../../components/admin/InvestorDetailsModal";
import {
  faEllipsisV,
  faMailBulk,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { authApi } from "../../services/authServices";
import { useAuth } from "../../providers/authProvider";
import InformationModal from "../../components/InformationModal";
import {
  useUserEmail,
  initialState as userEmailInitialState,
} from "../../providers/userEmailProvider";
import UserEmailModal from "../../components/admin/UserEmailModal";
import { utils } from "../../utils/utils";
import { CUSTOMER_SUPPORT, SPONSOR } from "../../utils/roles";
import Badge from "reactstrap/lib/Badge";
import AdvanceTableWrapper from "../../components/admin/advanceTable/AdvanceTableWrapper";
import AdvanceTable from "../../components/admin/advanceTable/AdvanceTable";
import AdvanceTablePagination from "../../components/admin/advanceTable/AdvanceTablePagination";

const states = require("../../assets/states.json");

const USER_STATUS_ACTIVE = "3";
const USER_ROLE_INVESTOR = 3;
const MAX_PAGE_SIZE = 100;

const TABLE_COLUMNS = [
  {
    accessor: "firstName",
    Header: "First Name",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { firstName } = rowData.row.original;
      return firstName || "-";
    },
  },
  {
    accessor: "lastName",
    Header: "Last Name",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { lastName } = rowData.row.original;
      return lastName || "-";
    },
  },
  {
    accessor: "name",
    Header: "Registered Name",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { name } = rowData.row.original;
      return name || "-";
    },
  },
  {
    accessor: "email",
    Header: "Email",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { email } = rowData.row.original;
      return email || "-";
    },
  },
  {
    accessor: "state",
    Header: "State",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { state } = rowData.row.original;
      return states.find((item) => item.abbreviation === state)?.name || "-";
    },
  },
  {
    accessor: "city",
    Header: "City",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { city } = rowData.row.original;
      return city || "-";
    },
  },
  {
    accessor: "zipcode",
    Header: "Zip",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { zipcode } = rowData.row.original;
      return zipcode || "-";
    },
  },
  {
    accessor: "address",
    Header: "Address",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { address } = rowData.row.original;
      return address || "-";
    },
  },
  {
    accessor: "phone",
    Header: "Phone",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { phone } = rowData.row.original;
      return phone || "-";
    },
  },
  {
    accessor: "lastActivity",
    Header: "Activity",
    headerProps: { className: "text-truncate" },
    Cell: (rowData) => {
      const { lastActivity } = rowData.row.original;
      return lastActivity ? (
        utils.formatDate(lastActivity, "MM/DD/YYYY HH:mm")
      ) : (
        <Badge color={"warning"}>No Activity</Badge>
      );
    },
  },
];

const Investors = () => {
  const [authContext, setAuthContext] = useAuth();
  const [investorsContext, setInvestorsContext] = useInvestors();
  const [investors, setInvestors] = useState({});
  const [selectedInvestor, setSelectedInvestor] = useState(false);
  const [generalLoading, setGeneralLoading] = useState(true);
  const [loading, setLoading] = useState({});
  const [refresh, setRefresh] = useState();
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });
  const [userEmailContext, setUserEmailContext] = useUserEmail();

  const user = authContext.currentUser;

  const getSizePerPage = (size) => (size === "All" ? MAX_PAGE_SIZE : size);

  const actions = (investor, loading) => {
    return loading[investor.id] ? (
      <div className="min-width-50">
        <Loader size="sm" align="end" />
      </div>
    ) : (
      <UncontrolledDropdown className="d-inline-block">
        <DropdownToggle color="white">
          <FontAwesomeIcon className="text-primary" icon={faEllipsisV} />
        </DropdownToggle>
        <DropdownMenu right>
          <DropdownItem onClick={() => setSelectedInvestor(investor)}>
            See Details
          </DropdownItem>
          <DropdownItem onClick={() => loginAs(investor)}>
            See Investor Portal
          </DropdownItem>
          <DropdownItem onClick={() => sendEmail(investor)}>
            Send Email
          </DropdownItem>
          <DropdownItem onClick={() => resendInvite(investor)}>
            Resend Invite
          </DropdownItem>
          <DropdownItem onClick={() => resetPassword(investor)}>
            Password Reset
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };

  const onSort = useCallback(
    ([data]) => {
      if (data) {
        const sortBy = data.id;
        const direction = data.desc ? "desc" : "asc";
        if (
          investorsContext.sortBy === sortBy?.id &&
          investorsContext.direction === direction
        ) {
          return;
        }
        setInvestorsContext({
          sortBy,
          direction,
        });
      } else {
        setInvestorsContext({
          sortBy: null,
          direction: null,
        });
      }
    },
    [investorsContext.direction, investorsContext.sortBy, setInvestorsContext]
  );

  useEffect(() => {
    setGeneralLoading(true);
    const hasCSRole = utils.userHasRole(user, CUSTOMER_SUPPORT);
    const hasSponsorRole = utils.userHasRole(user, SPONSOR);
    usersApi
      .getUsers(
        {
          role: USER_ROLE_INVESTOR,
          search: investorsContext.search,
          page: investorsContext.page - 1,
          pageSize: getSizePerPage(investorsContext.sizePerPage),
          sortBy: investorsContext.sortBy,
          direction: investorsContext.direction,
        },
        hasCSRole ? "customer-support" : hasSponsorRole ? "sponsor" : "admin"
      )
      .then((result) => {
        setInvestors(result);
        setGeneralLoading(false);
      });
  }, [
    refresh,
    investorsContext.search,
    investorsContext.page,
    investorsContext.sizePerPage,
    investorsContext.sortBy,
    investorsContext.direction,
    user,
  ]);

  const loginAs = (investor) => {
    setLoading({ [investor.id]: true });
    const hasCSRole = utils.userHasRole(user, CUSTOMER_SUPPORT);
    const hasSponsorRole = utils.userHasRole(user, SPONSOR);
    authApi
      .loginAs(
        { userId: investor.id },
        hasCSRole ? "customer-support" : hasSponsorRole ? "sponsor" : "admin"
      )
      .then((result) => {
        setAuthContext({ currentUser: { ...result } });
        setLoading({ [investor.id]: false });
        window.open(
          `${window.location.origin}/${
            utils.userHasRole(investor, "advisor")
              ? "investor-selector"
              : "portal"
          }`,
          "_self"
        );
      });
  };

  const resendInvite = (investor) => {
    if (investor.status === USER_STATUS_ACTIVE) {
      return setInformationModal({
        isOpen: true,
        title: "Heads up!",
        body: "User is already active.",
      });
    }
    setLoading({ [investor.id]: true });
    usersApi
      .resendInvite({ id: investor.id })
      .then(() => {
        setInformationModal({
          isOpen: true,
          title: "Alright!",
          body: "Invite resent successfully.",
        });
        setLoading({ [investor.id]: false });
      })
      .catch((err) => {
        setInformationModal({
          isOpen: true,
          title: "Oops, there was an error with your request",
          body: "There was an error sending email.",
        });
        setLoading({ [investor.id]: false });
      });
  };

  const resetPassword = (investor) => {
    if (investor.status !== USER_STATUS_ACTIVE) {
      return setInformationModal({
        isOpen: true,
        title: "Heads up!",
        body: "User is not active.",
      });
    }
    setLoading({ [investor.id]: true });
    usersApi.resetPassword({ email: investor.email }).then(() => {
      setInformationModal({
        isOpen: true,
        title: "Alright!",
        body: "Reset email sent successfully.",
      });
      setLoading({ [investor.id]: false });
    });
  };

  const sendEmail = (user) => {
    setUserEmailContext({ ...userEmailInitialState, user, isActive: true });
  };

  const sendEmailAll = () => {
    const user = investors.data;
    setUserEmailContext({
      ...userEmailInitialState,
      user,
      isActive: true,
      type: "Investor",
    });
  };

  return (
    <Container fluid>
      <Header>
        <HeaderTitle className="d-inline-block">Investors</HeaderTitle>
        <Button color="info" className="m-2 float-right" onClick={sendEmailAll}>
          <FontAwesomeIcon icon={faMailBulk} />
          <span className="ml-2">Email All</span>
        </Button>
      </Header>
      <Row>
        <Col>
          <Card>
            <CardHeader className="pb-0">
              <div className="d-flex align-items-center justify-content-between">
                <InputGroup className="mr-3 col-4 px-0" size="m">
                  <Input
                    maxLength="50"
                    value={investorsContext.search}
                    onChange={(evt) =>
                      setInvestorsContext({ search: evt.target.value, page: 1 })
                    }
                    className="border-0"
                    placeholder="Search for.."
                  />
                </InputGroup>
                <div className="d-flex align-items-center">
                  <small className="text-muted mr-2">
                    Showing {investors?.count} investors
                  </small>
                  <Button
                    size="sm"
                    className="mr-2 rounded-circle d-flex custom-rounded-button"
                    color="primary"
                    onClick={() => setRefresh(!refresh)}
                  >
                    <FontAwesomeIcon icon={faSync} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {generalLoading ? (
                <Loader />
              ) : investors.data.length ? (
                <AdvanceTableWrapper
                  columns={[
                    ...TABLE_COLUMNS,
                    {
                      accessor: "id",
                      Header: "",
                      width: 15,
                      headerProps: { className: "text-right text-truncate" },
                      cellProps: {
                        className: "text-center p-0",
                      },
                      disableSortBy: true,
                      Cell: (rowData) => actions(rowData.row.original, loading),
                    },
                  ]}
                  data={investors.data || []}
                  sortable
                  onSort={onSort}
                  pageSize={getSizePerPage(investorsContext.sizePerPage)}
                  defaultSort={{
                    sortBy: investorsContext.sortBy,
                    direction: investorsContext.direction,
                  }}
                >
                  <Card className="d-flex">
                    <CardBody className="overflow-auto min-height-300p">
                      {investors?.data?.length ? (
                        <AdvanceTable
                          table
                          isLoading={generalLoading}
                          headerClassName="small"
                          tableProps={{
                            striped: false,
                            className: "mb-0",
                          }}
                        />
                      ) : (
                        <div className="text-center">No results</div>
                      )}
                    </CardBody>
                    <CardFooter>
                      <AdvanceTablePagination
                        totalCount={investors?.count}
                        pageCount={investors?.totalPages}
                        currentPage={investorsContext.page - 1}
                        onPageChange={(page) => setInvestorsContext({ page })}
                        pageSize={getSizePerPage(investorsContext.sizePerPage)}
                        onPageSizeChange={(sizePerPage) =>
                          setInvestorsContext({ page: 1, sizePerPage })
                        }
                        rowsPerPageOptions={[5, 10, 25, 50]}
                      />
                    </CardFooter>
                  </Card>
                </AdvanceTableWrapper>
              ) : (
                <div className="text-center">No results</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      {userEmailContext.isActive ? (
        <UserEmailModal
          onClose={() => setUserEmailContext({ isActive: false })}
        />
      ) : null}
      {informationModal?.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={() =>
            setInformationModal({ isOpen: false, title: "", body: "" })
          }
        />
      ) : null}
      {selectedInvestor ? (
        <InvestorModalDetails
          investor={selectedInvestor}
          onSubmit={(investor) => {
            investors.data.splice(
              investors.data.findIndex((i) => i.id === investor.id),
              1,
              investor
            );
            setInvestors({ ...investors });
          }}
          onClose={() => setSelectedInvestor(false)}
        />
      ) : null}
    </Container>
  );
};

export default Investors;
